import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "./ui/alert-dialog";

const ConfirmContext = createContext(() => Promise.resolve(true));
export const useConfirm = () => useContext(ConfirmContext);

// Promise-based confirmation dialog usable from any component:
//   const confirm = useConfirm();
//   if (!(await confirm({ title, description }))) return;
export const ConfirmProvider = ({ children }) => {
  const { t } = useTranslation();
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setState({
      title: opts.title || t("confirm.deleteTitle"),
      description: opts.description || t("confirm.deleteDesc"),
      confirmText: opts.confirmText || t("confirm.confirm"),
      cancelText: opts.cancelText || t("confirm.cancel"),
      destructive: opts.destructive !== false,
    });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, [t]);

  const close = (val) => {
    setState(null);
    if (resolver.current) { resolver.current(val); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) close(false); }}>
        {state && (
          <AlertDialogContent data-testid="confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">{state.title}</AlertDialogTitle>
              <AlertDialogDescription>{state.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="confirm-cancel" onClick={() => close(false)}>
                {state.cancelText}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-accept"
                onClick={() => close(true)}
                className={state.destructive ? "bg-red-600 hover:bg-red-700 text-white" : ""}
              >
                {state.confirmText}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};
